import { useState } from "react";
import styled from "styled-components";
import {
  COLORS,
  CONSTANTS,
  FONTSIZE,
  FONTWEIGHT,
} from "helpers/designConstants";
import { MiniButton } from "components/standardElements";
import PDFExportModal from "./PDFExportModal";
import StockIssueDetailDialog from "./StockIssueDetailDialog";
import { projectHasDrift, computeIssueDrift } from "./contactDrift";

const MAX_THUMBS = 6;

function countBySeverity(issues) {
  const counts = { critical: 0, warning: 0 };
  for (const i of issues) {
    if (counts[i.severity] != null) counts[i.severity] += 1;
  }
  return counts;
}

// Worst severity → the accent color of the card's left rail.
function worstSeverity(counts) {
  if (counts.critical > 0) return "critical";
  if (counts.warning > 0) return "warning";
  return "info";
}

// Pick up to N unique product thumbnails, severity-first. Returns thumbnail
// URLs and the number of extra products beyond the cap.
function pickThumbnails(issues, productsById, limit) {
  const seen = new Set();
  const thumbs = [];
  const ordered = [...issues].sort((a, b) => {
    const rank = { critical: 0, warning: 1, info: 2 };
    return (rank[a.severity] ?? 99) - (rank[b.severity] ?? 99);
  });
  for (const issue of ordered) {
    const product = productsById[issue.product_id];
    if (!product?.imageURL) continue;
    if (seen.has(issue.product_id)) continue;
    seen.add(issue.product_id);
    thumbs.push({
      id: issue.product_id,
      src: product.imageURL,
      alt: product.name || product.itemNumber || "",
    });
    if (thumbs.length === limit) break;
  }
  const uniqueProducts = new Set(issues.map((i) => i.product_id));
  const extra = Math.max(0, uniqueProducts.size - thumbs.length);
  return { thumbs, extra };
}

export default function StockIssueProjectCard({
  project,
  productsById,
  onUpdateStatus,
  onBatchUpdate,
}) {
  const [busy, setBusy] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const severityCounts = countBySeverity(project.issues);
  const severity = worstSeverity(severityCounts);
  const totalIssues = project.issues.length;
  const hasDrift = projectHasDrift(project);
  const { thumbs, extra } = pickThumbnails(
    project.issues,
    productsById,
    MAX_THUMBS,
  );

  // Mark contacted at the project level covers everything the rep just
  // discussed with the customer: brand-new issues AND already-contacted
  // issues that have drifted since the last call (re-stamping the snapshot
  // clears the Updated chip). Dismissed rows stay dismissed — noise
  // suppression is a separate intent.
  const eligibleForContacted = project.issues
    .filter((i) => {
      if (i.status === "new" || i.status === "acknowledged") return true;
      if (i.status === "contacted") return computeIssueDrift(i).drifted;
      return false;
    })
    .map((i) => i.id);

  const runBatch = async (ids, status) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const result = await onBatchUpdate(ids, status);
      const updated = result.updated?.length ?? 0;
      const skipped = result.skipped?.length ?? 0;
      if (skipped > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `Batch ${status}: ${updated} updated, ${skipped} skipped`,
          result.skipped
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const handleMarkContacted = () => runBatch(eligibleForContacted, "contacted");

  // Actions live inside the card but must not trigger the card's click-to-
  // open. Everything that shouldn't bubble uses stopPropagation.
  const stopAction = (e) => e.stopPropagation();

  const metaLine = [project.distributor, project.accountNumber, project.rep]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <Card
        role="button"
        tabIndex={0}
        $severity={severity}
        onClick={() => setShowDetail(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setShowDetail(true);
          }
        }}
        aria-label={`Open ${project.name} detail`}
      >
        <AccentRail $severity={severity} />

        <CardBody>
          <HeaderSection>
            <TitleRow>
              <Logo>
                <img
                  src={project.logoURL || "/stormtech_icon_placeholder.svg"}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/stormtech_icon_placeholder.svg";
                  }}
                />
              </Logo>
              <TitleBlock>
                <ProjectName>{project.name}</ProjectName>
                {metaLine && <ProjectMeta>{metaLine}</ProjectMeta>}
              </TitleBlock>
            </TitleRow>
            <CountGroup>
              {severityCounts.critical > 0 && (
                <SeverityChip tone="critical">
                  {severityCounts.critical} Critical
                </SeverityChip>
              )}
              {severityCounts.warning > 0 && (
                <SeverityChip tone="warning">
                  {severityCounts.warning} Warning
                </SeverityChip>
              )}
              {hasDrift && <UpdatedChip>Updated</UpdatedChip>}
              <TotalCount>
                {totalIssues} issue{totalIssues === 1 ? "" : "s"}
              </TotalCount>
            </CountGroup>
          </HeaderSection>

          <Divider />

          <FooterSection>
            <ThumbStrip aria-hidden>
              {thumbs.map((t) => (
                <Thumb key={t.id}>
                  <img src={t.src} alt="" loading="lazy" />
                </Thumb>
              ))}
              {extra > 0 && <ThumbMore>+{extra}</ThumbMore>}
            </ThumbStrip>
            <ActionRow onClick={stopAction}>
              <MiniButton type="button" onClick={() => setShowPdfModal(true)}>
                Export PDF
              </MiniButton>
              <MiniButton
                type="button"
                onClick={handleMarkContacted}
                disabled={busy || eligibleForContacted.length === 0}
              >
                {busy ? "Marking..." : "Mark contacted"}
              </MiniButton>
            </ActionRow>
          </FooterSection>
        </CardBody>
      </Card>

      <StockIssueDetailDialog
        show={showDetail}
        onClose={() => setShowDetail(false)}
        project={project}
        productsById={productsById}
        onUpdateStatus={onUpdateStatus}
        onBatchUpdate={onBatchUpdate}
      />

      <PDFExportModal
        show={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        project={project}
        productsById={productsById}
      />
    </>
  );
}

// Severity color lookup for the left rail + hover border.
const severityColor = (theme) => ({
  critical: COLORS.alertRed,
  warning: COLORS.alertOrange,
  info: COLORS.stormtechGrey,
}[theme] || COLORS.stormtechGrey);

const Card = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  border-radius: ${CONSTANTS.borderRadius};
  border: 1px solid ${COLORS.secondary};
  background: ${COLORS.primaryInverted};
  color: ${COLORS.text};
  cursor: pointer;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;

  &:hover {
    border-color: ${({ $severity }) => severityColor($severity)};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.stormtechOrange};
    outline-offset: 1px;
  }
`;

const AccentRail = styled.div`
  flex-shrink: 0;
  width: 4px;
  background: ${({ $severity }) => severityColor($severity)};
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  min-width: 0;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Logo = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${COLORS.secondary};
  margin: 0;
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
`;

const TitleBlock = styled.div`
  flex: 1 1 200px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProjectName = styled.div`
  font-size: ${FONTSIZE.medium};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProjectMeta = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CountGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const SeverityChip = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ tone }) =>
    tone === "critical" ? COLORS.alertRed : COLORS.alertOrange};
  background: ${({ tone }) =>
    tone === "critical" ? "rgba(204, 0, 0, 0.14)" : "rgba(247, 147, 30, 0.18)"};
  border: 0;
`;

const TotalCount = styled.span`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: ${FONTWEIGHT.medium};
`;

// Neutral chip that surfaces drift-since-contact on contacted rows. Always
// the same color/shape regardless of whether the drift is good or bad news —
// consistency of signal over editorial direction. The rep opens the sheet
// to see what changed.
const UpdatedChip = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${COLORS.text};
  background: ${COLORS.tertiary};
  border: 1px solid ${COLORS.secondary};
`;

const ThumbStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Thumb = styled.div`
  width: 44px;
  height: 44px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const ThumbMore = styled.div`
  font-size: ${FONTSIZE.xsmall};
  font-weight: ${FONTWEIGHT.medium};
  color: ${COLORS.stormtechGrey};
  padding: 0 6px;
  text-transform: lowercase;
  letter-spacing: 0.02em;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
`;
